// src/components/CampaignIntelligenceSummary.jsx
import { useEffect, useState } from "react";
import {
  Box,
  Text,
  Spinner,
  VStack,
  Badge,
  Divider,
  Flex,
} from "@chakra-ui/react";
import { supabase } from "../utils/supabaseClient";
import CampaignROIExport from "./CampaignROIExport";
import { getRoiBadge, getTrendIcon } from "./flyer/roiUtils";

/**
 * CampaignIntelligenceSummary
 * Aggregates campaign intelligence metrics for the current business.
 * - Calls RPC update_campaign_intelligence(p_auth_id uuid)
 *   which returns:
 *   postcode_sector, households, slots_sold, slots_available,
 *   roi_estimate (expected redemptions), redemption_velocity_7d
 */
export default function CampaignIntelligenceSummary({ businessId, campaignId }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchIntelligence = async () => {
      if (!businessId) return;

      setLoading(true);
      try {
        const { data, error } = await supabase.rpc(
          "update_campaign_intelligence",
          { p_auth_id: businessId }
        );

        if (error) throw error;

        setData(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to fetch campaign intelligence:", err);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchIntelligence();
  }, [businessId]);

  return (
    <Box p={4} bg="gray.50" borderRadius="md">
      <Flex justify="space-between" align="center" mb={2}>
        <Text fontWeight="bold">📊 Campaign Intelligence Summary</Text>
        <Flex align="center" gap={2}>
          <Badge colorScheme="green" variant="subtle">
            🤖 Trust Our AI
          </Badge>
          {businessId && campaignId && (
            <CampaignROIExport businessId={businessId} campaignId={campaignId} />
          )}
        </Flex>
      </Flex>

      {loading ? (
        <Spinner />
      ) : data.length === 0 ? (
        <Text fontSize="sm" color="gray.500">
          No intelligence available.
        </Text>
      ) : (
        <VStack align="start" spacing={3}>
          {data.map((item) => {
            const roiValue = item.roi_estimate || 0;
            const roiBadge = getRoiBadge(roiValue);
            const trendIcon = getTrendIcon(roiValue, 0); // no prev_14d here yet

            return (
              <Box key={item.postcode_sector} w="100%">
                <Text fontSize="sm">
                  <strong>{item.postcode_sector}</strong>
                  <br />
                  🏠 Households: {item.households ?? "—"}
                  <br />
                  📦 Slots: {item.slots_sold ?? 0} / 30 sold •{" "}
                  {item.slots_available ?? 0} available
                  <br />
                  💰 ROI (expected redemptions):{" "}
                  {item.roi_estimate !== null && item.roi_estimate !== undefined
                    ? roiValue.toFixed(0)
                    : "—"}{" "}
                  {trendIcon} • Velocity:{" "}
                  {item.redemption_velocity_7d ?? "—"} scans/week
                  <Badge ml={2} backgroundColor={roiBadge.color} color="white">
                    {roiBadge.label}
                  </Badge>
                </Text>
                <Divider />
              </Box>
            );
          })}
        </VStack>
      )}
    </Box>
  );
}
